import React from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { DeleteNodeCommand } from '../../core/commands/DeleteNodeCommand';
import { Button } from '../../components/ui/button';
import { Trash2 } from 'lucide-react';
import { useDiagramStore } from '../../stores/diagramStore';
import { DeleteEdgeCommand } from '../../core/commands/edges/DeleteEdgeCommand';
import { CompositeCommand } from '../../core/commands/ICommand';
import { useTableCellStore } from '../../stores/tableCellStore';
import { ClearTableCellTextsCommand } from '../../core/commands/ClearTableCellTextsCommand';
import { getTableCellRectByKey } from '../../domains/diagram/shapes/table/tableDrop.utils';
import type { FrameManager } from '../../core/frames/FrameManager';
import { DeleteFrameCascadeCommand } from '../../core/commands/DeleteFrameCascadeCommand';

/**
 * Delete Tool Component
 */
const DeleteToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const selectedIds = Array.from(context.selection);
  const isDisabled = selectedIds.length === 0 && !context.selectedFrameId;
  
  return React.createElement(Button, {
    onClick: () => onExecute({ nodeIds: selectedIds }),
    disabled: isDisabled,
    variant: 'destructive',
    size: 'sm',
    className: 'flex items-center gap-2'
  }, [
    React.createElement(Trash2, { key: 'icon', className: 'h-4 w-4' }),
    React.createElement('span', { key: 'text' }, 
      selectedIds.length > 1 
        ? `Delete ${selectedIds.length} items` 
        : 'Delete'
    )
  ]);
};

/**
 * Tool for deleting selected nodes and their connected edges.
 * Follows proper command pattern for undo/redo support.
 */
export class DeleteTool extends BaseTool {
  constructor(private frameManager?: FrameManager) {
    super();
  }

  setFrameManager(frameManager: FrameManager): void {
    this.frameManager = frameManager;
  }

  manifest: IToolManifest = {
    id: 'delete',
    name: 'tools.delete',
    icon: 'trash',
    category: 'edit',
    description: 'Delete selected items',
    version: '1.0.0',
    
    requiresSelection: true,
    minSelection: 1,
    
    ui: {
      toolbar: true,
      contextMenu: true,
      propertyPanel: false
    },
    
    capabilities: {
      modifiesShapes: false,
      createsShapes: false,
      deletesShapes: true,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: true,
      requiresUserInput: false,
      supportsMultiSelect: true,
      supportsBatchOperations: true
    },
    
    shortcuts: [{
      key: 'Delete',
      modifiers: [],
      description: 'Delete selected items'
    }, {
      key: 'Backspace',
      modifiers: [],
      description: 'Delete selected items'
    }]
  };

  canExecute(context: IToolContext): boolean {
    // Genograms own cascade/frame deletion through GenogramDeleteTool.
    if (context.diagramType === 'genogram') return false;
    if (context.selectedFrameId && context.selection.size === 0 && this.frameManager) {
      return context.canWrite && new DeleteFrameCascadeCommand(this.frameManager, context.selectedFrameId).canExecute();
    }
    if (!super.canExecute(context)) return false;
    return true;
  }

  execute(params?: { nodeIds?: string[] }): ICommand | null {
    const store = useDiagramStore.getState();
    if (this.context?.selectedFrameId && this.context.selection.size === 0 && this.frameManager && !params?.nodeIds?.length) {
      if (!this.canExecute(this.context)) return null;
      return new DeleteFrameCascadeCommand(this.frameManager, this.context.selectedFrameId);
    }

    let selectedIds: string[];
    if (!params?.nodeIds || params.nodeIds.length === 0) {
      if (!this.context || this.context.selection.size === 0) {
        return null;
      }
      selectedIds = Array.from(this.context.selection);
    } else {
      selectedIds = params.nodeIds;
    }

    // Separate nodes and edges based on current store state
    const nodeIds = selectedIds.filter(id => store.nodes.some(n => n.id === id));
    const edgeIds = selectedIds.filter(id => store.edges.some(e => e.id === id));

    const commands: ICommand[] = [];
    let nodeIdsToDelete: string[] = [];

    const cellSelections = useTableCellStore.getState().selections;
    const selectedNodes = nodeIds.map((id) => store.nodes.find((n) => n.id === id)).filter(Boolean) as any[];
    const tablesWithCellSelection = selectedNodes
      .filter((n) => n.type === 'table')
      .map((t) => {
        const sel = cellSelections[t.id];
        const keys = sel?.selectedKeys ?? [];
        return { table: t as any, keys };
      })
      .filter((x) => x.keys.length > 0);

    if (tablesWithCellSelection.length > 0) {
      const tableIds = new Set(tablesWithCellSelection.map((x) => x.table.id as string));

      const dockedToDelete: string[] = [];
      const clearTextCommands: ICommand[] = [];
      const tablesToDelete: string[] = [];

      tablesWithCellSelection.forEach(({ table, keys }) => {
        const normalizedKeys = keys
          .map((k) => getTableCellRectByKey(table, k as any)?.cellKey)
          .filter(Boolean) as any[];

        const keySet = new Set(normalizedKeys);
        const attrs = (table.attrs ?? {}) as any;
        const textMap = (attrs.cellText ?? {}) as Record<string, string>;
        const styleMap = (attrs.cellStyles ?? {}) as Record<string, unknown>;

        let hasAnyContent = false;
        for (const k of normalizedKeys) {
          const t = textMap[k];
          if (typeof t === 'string' && t.trim().length > 0) {
            hasAnyContent = true;
            break;
          }
          if (Object.prototype.hasOwnProperty.call(styleMap, k)) {
            hasAnyContent = true;
            break;
          }
        }

        if (!hasAnyContent) {
          for (const n of store.nodes as any[]) {
            const dock = n?.attrs?.tableDock;
            if (!dock) continue;
            if (dock.tableId !== table.id) continue;
            if (keySet.has(dock.cellKey)) {
              hasAnyContent = true;
              break;
            }
          }
        }

        if (!hasAnyContent) {
          tablesToDelete.push(table.id);
          return;
        }

        if (normalizedKeys.length > 0) {
          clearTextCommands.push(new ClearTableCellTextsCommand({ nodeId: table.id, cellKeys: normalizedKeys }));
        }

        store.nodes.forEach((n: any) => {
          const dock = n?.attrs?.tableDock;
          if (!dock) return;
          if (dock.tableId !== table.id) return;
          if (keySet.has(dock.cellKey)) dockedToDelete.push(n.id);
        });
      });

      const remainingNodeIds = nodeIds.filter((id) => !tableIds.has(id));
      const deleteNodeIds = [
        ...remainingNodeIds,
        ...Array.from(new Set(dockedToDelete)),
        ...Array.from(new Set(tablesToDelete)),
      ];
      nodeIdsToDelete = deleteNodeIds;

      if (clearTextCommands.length > 0) {
        commands.push(...clearTextCommands);
      }
      if (deleteNodeIds.length > 0) {
        const deleteCommand = this.context?.createDeleteNodeCommand
          ? this.context.createDeleteNodeCommand(deleteNodeIds)
          : new DeleteNodeCommand(deleteNodeIds);
        commands.push(deleteCommand);
      }
    } else if (nodeIds.length > 0) {
      // Use context's command factory if available (enables comment restoration), otherwise create directly
      nodeIdsToDelete = nodeIds;
      const deleteCommand = this.context?.createDeleteNodeCommand
        ? this.context.createDeleteNodeCommand(nodeIds)
        : new DeleteNodeCommand(nodeIds);
      commands.push(deleteCommand);
    }

    // Only delete standalone edges (not connected to nodes being deleted in same op)
    const nodeIdSet = new Set(nodeIdsToDelete);
    const standaloneEdgeIds = edgeIds.filter(edgeId => {
      const edge = store.edges.find(e => e.id === edgeId);
      return edge && !nodeIdSet.has(edge.from) && !nodeIdSet.has(edge.to);
    });

    standaloneEdgeIds.forEach(id => {
      commands.push(new DeleteEdgeCommand(id));
    });

    if (commands.length === 0) return null;

    if (commands.length === 1) return commands[0];

    return new CompositeCommand('Delete Items', commands);
  }

  getToolbarComponent() {
    return DeleteToolComponent;
  }

  getContextMenuComponent() {
    return DeleteToolComponent;
  }
}
